import type { FirebaseApp } from 'firebase/app';
import {
  connectFunctionsEmulator,
  getFunctions,
  httpsCallable,
  type Functions,
} from 'firebase/functions';
import type { EmulatorEndpoint } from '@taki/runtime-config';

/**
 * LES COMPTES, COTE APP : creer, redonner un mot de passe, retirer.
 *
 * CE QUE CE CLIENT NE FAIT PAS, ET NE PEUT PAS FAIRE : ecrire quoi que ce soit.
 * Creer un compte Firebase Auth, poser des custom claims et couper les sessions
 * ouvertes de quelqu'un n'existent QUE dans l'Admin SDK, cote serveur. Ce
 * fichier ne fait que poster une demande a `gererUtilisateur`, qui etablit
 * elle-meme qui appelle (docs/FUNCTIONS.md).
 *
 * LA DIFFERENCE AVEC `sdk.members`, et il ne faut pas la confondre :
 *   - `sdk.members` ecrit la FICHE, c'est-a-dire les DROITS. Elle suffit a
 *     INVITER quelqu'un : sa fiche l'attend, il entrera avec Google.
 *   - `sdk.comptes` fabrique l'IDENTIFIANT, c'est-a-dire de quoi entrer sans
 *     Google. C'est le deuxieme chemin, pas le premier.
 *
 * LES TYPES CI-DESSOUS SONT UNE RECOPIE de `functions/src/acces/contrat.ts` :
 * `functions/` ne peut dependre d'aucun `@taki/*` (le deploiement Firebase y
 * lance npm, ou aucun `workspace:*` ne se resout). Deux garde-fous :
 * `ACCES_CONTRACT_VERSION` est compare a chaque reponse, et
 * `tests/acces-contrat.test.mjs` lit LES DEUX fichiers.
 */

/* -------------------------------------------------------------------------- */
/* Le contrat (recopie de functions/src/acces/contrat.ts)                      */
/* -------------------------------------------------------------------------- */

/** Version de la FORME des reponses attendue par ce client. */
export const ACCES_CONTRACT_VERSION = 1;

/**
 * Region des Cloud Functions. DOIT valoir celle de `functions/src/runtime.ts`.
 * Viser la mauvaise region fait echouer l'appel sur un « Failed to fetch ».
 */
export const ACCES_FUNCTIONS_REGION = 'europe-west1';

/** Le nom de la callable de gestion des comptes. */
export const CALLABLE_COMPTES = 'gererUtilisateur';

/** Les trois gestes, et pas un de plus. */
export type OperationCompte = 'creer' | 'mot-de-passe' | 'retirer';

/** Ce que rend une operation de compte. */
export interface ResultatCompte {
  op: OperationCompte;
  /** L'adresse traitee, en minuscules. */
  email: string;
  /** L'uid du compte Auth, quand il y en a un. */
  uid: string | null;
  /**
   * Le mot de passe TIRE AU SORT, rendu UNE SEULE FOIS. Il n'existe nulle part
   * ailleurs : ni en base, ni dans un journal. Montre-le, ou il est perdu.
   */
  motDePasse: string | null;
  /** Le compte Auth vient-il d'etre cree par cet appel ? */
  compteCree: boolean;
  /** La fiche membre vient-elle d'etre creee par cet appel ? */
  ficheCreee: boolean;
}

/* -------------------------------------------------------------------------- */
/* Le client                                                                   */
/* -------------------------------------------------------------------------- */

/** Ce qu'une erreur de compte dit a l'ecran, en plus de son message. */
export class ComptesError extends Error {
  /** Le code de la callable (`permission-denied`, `not-found`...), ou `unknown`. */
  readonly code: string;
  /** La raison precise posee par la Function (`owner-reserved`, `soi-meme`...). */
  readonly raison: string | null;

  constructor(message: string, code: string, raison: string | null) {
    super(message);
    this.name = 'ComptesError';
    this.code = code;
    this.raison = raison;
  }
}

/** Ce qu'une app peut demander sur un compte. */
export interface ComptesClient {
  /**
   * Cree le compte Auth ET sa fiche membre (ou complete ce qui manque), et rend
   * le mot de passe tire au sort quand il a fallu en tirer un.
   */
  creer(entree: {
    email: string;
    orgRole: string;
    displayName?: string | undefined;
    /** Un mot de passe choisi. Absent : il est tire au sort et rendu. */
    motDePasse?: string | undefined;
  }): Promise<ResultatCompte>;
  /**
   * Pose un mot de passe neuf, coupe les sessions ouvertes, et CREE le compte
   * s'il manquait (un invite qui n'est jamais entre par Google).
   */
  motDePasse(email: string): Promise<ResultatCompte>;
  /**
   * Retire l'acces : fiche archivee, claims retires, sessions coupees. Le
   * compte Auth survit, sans organisation.
   */
  retirer(email: string): Promise<ResultatCompte>;
}

/** Instances Functions deja branchees sur l'emulateur (cf. `createMarketDataClient`). */
const emulatorBoundFunctions = new WeakSet<Functions>();

/**
 * Fabrique le client des comptes.
 *
 * @param app - L'application Firebase partagee.
 * @param emulator - `config.emulators?.functions` en dev local, sinon omis.
 */
export function createComptesClient(
  app: FirebaseApp,
  emulator?: EmulatorEndpoint | undefined,
): ComptesClient {
  const functions = getFunctions(app, ACCES_FUNCTIONS_REGION);

  if (emulator && !emulatorBoundFunctions.has(functions)) {
    connectFunctionsEmulator(functions, emulator.host, emulator.port);
    emulatorBoundFunctions.add(functions);
    console.info(
      `[@taki/sdk] Cloud Functions branchees sur l'emulateur ${emulator.host}:${emulator.port}.`,
    );
  }

  const appeler = httpsCallable<unknown, unknown>(functions, CALLABLE_COMPTES);

  async function call(payload: Record<string, unknown>): Promise<ResultatCompte> {
    let brut: unknown;
    try {
      const reponse = await appeler(payload);
      brut = reponse.data;
    } catch (cause: unknown) {
      throw enErreur(cause);
    }

    const data =
      typeof brut === 'object' && brut !== null ? (brut as Record<string, unknown>) : {};
    if (data['contractVersion'] !== ACCES_CONTRACT_VERSION) {
      throw new ComptesError(
        `Les Cloud Functions repondent en version ${String(data['contractVersion'])}, ` +
          `cette app attend la version ${ACCES_CONTRACT_VERSION}. Le socle et les Functions ` +
          'ne sont pas deployes ensemble : il faut redeployer les Functions.',
        'failed-precondition',
        'version-de-contrat',
      );
    }

    return {
      op: data['op'] as OperationCompte,
      email: typeof data['email'] === 'string' ? data['email'] : '',
      uid: typeof data['uid'] === 'string' ? data['uid'] : null,
      motDePasse: typeof data['motDePasse'] === 'string' ? data['motDePasse'] : null,
      compteCree: data['compteCree'] === true,
      ficheCreee: data['ficheCreee'] === true,
    };
  }

  return {
    creer: (entree) =>
      call({
        op: 'creer',
        email: entree.email,
        orgRole: entree.orgRole,
        ...(entree.displayName != null ? { displayName: entree.displayName } : {}),
        ...(entree.motDePasse != null ? { motDePasse: entree.motDePasse } : {}),
      }),
    motDePasse: (email) => call({ op: 'mot-de-passe', email }),
    retirer: (email) => call({ op: 'retirer', email }),
  };
}

/**
 * Traduit l'echec d'une callable en quelque chose qu'un ecran sait montrer.
 *
 * Le cas qui compte : une Function PAS DEPLOYEE rend `not-found` avec un
 * message anglais qui ne dit rien a personne. C'est l'etat normal d'un projet
 * ou personne n'a encore fait `firebase deploy --only functions`, et
 * l'ecran doit le dire dans ces termes-la, pas « erreur interne ».
 */
function enErreur(cause: unknown): ComptesError {
  const objet = typeof cause === 'object' && cause !== null ? cause : {};
  const code = String(Reflect.get(objet, 'code') ?? 'unknown').replace(/^functions\//, '');
  const details = Reflect.get(objet, 'details');
  const raison =
    typeof details === 'object' && details !== null
      ? ((Reflect.get(details, 'reason') as string | undefined) ?? null)
      : null;
  const message = String(Reflect.get(objet, 'message') ?? cause);

  if (code === 'not-found' && raison === null) {
    return new ComptesError(
      "La gestion des comptes n'est pas disponible dans cet environnement : " +
        'les Cloud Functions ne sont pas deployees.',
      code,
      'functions-absentes',
    );
  }
  if (code === 'internal' || code === 'unavailable') {
    return new ComptesError(
      "Les Cloud Functions n'ont pas repondu. Rien n'a ete change.",
      code,
      'functions-injoignables',
    );
  }
  return new ComptesError(message === '' ? "Echec de l'operation." : message, code, raison);
}
